'use client';

import { c as _c } from "react/compiler-runtime";
import type { User } from '@/lib/pr-v2/timeline-types';
import { User as UserIcon, Users } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { MentionSuggestion } from '../utils/mention-suggestions';
import { getMentionSuggestions } from '../utils/mention-suggestions';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { useOrgTeams } from '@/data/teams/use-org-teams';
import { cn } from '@/lib/utils';
type MentionableUsersDropdownProps = {
  users: User[];
  owner: string;
  onSelect: (user: {
    id: string;
    name: string;
  }) => void;
  onHighlight?: (user: {
    id: string;
    name: string;
  } | null) => void;
  searchQuery: string;
};

// Memoize the CommandItem to prevent re-renders
const MemoizedCommandItem = memo(t0 => {
  const $ = _c(20);
  const {
    suggestion,
    onSelect,
    isSelected
  } = t0;
  const t1 = suggestion.handle;
  const t2 = isSelected && "bg-accent";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex items-center gap-2 cursor-pointer", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== suggestion) {
    t4 = <MentionAvatar suggestion={suggestion} />;
    $[2] = suggestion;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== suggestion.handle) {
    t5 = <span className="truncate">{suggestion.handle}</span>;
    $[4] = suggestion.handle;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== suggestion.kind || $[7] !== suggestion.name) {
    t6 = suggestion.kind === "team" && <span className="truncate text-xs text-muted-foreground">{suggestion.name}</span>;
    $[6] = suggestion.kind;
    $[7] = suggestion.name;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className="min-w-0 flex flex-col">{t5}{t6}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t4 || $[13] !== t7) {
    t8 = <div className="flex flex-1 items-center gap-2">{t4}{t7}</div>;
    $[12] = t4;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== onSelect || $[16] !== suggestion.handle || $[17] !== t3 || $[18] !== t8) {
    t9 = <CommandItem value={t1} onSelect={onSelect} className={t3}>{t8}</CommandItem>;
    $[15] = onSelect;
    $[16] = suggestion.handle;
    $[17] = t3;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
});
function MentionAvatar(t0) {
  const $ = _c(4);
  const {
    suggestion
  } = t0;
  if (suggestion.kind === "team") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Users className="size-4 text-muted-foreground" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== suggestion.avatarUrl || $[2] !== suggestion.handle) {
    t1 = suggestion.avatarUrl ? <Avatar className="size-4"><AvatarImage src={suggestion.avatarUrl} alt={suggestion.handle} /><AvatarFallback>{suggestion.handle.charAt(0)}</AvatarFallback></Avatar> : <UserIcon className="size-4 text-muted-foreground" />;
    $[1] = suggestion.avatarUrl;
    $[2] = suggestion.handle;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// Memoize the entire dropdown component
export const MentionableUsersDropdown = memo(t0 => {
  const $ = _c(22);
  const {
    users,
    owner,
    onSelect,
    onHighlight,
    searchQuery
  } = t0;
  const {
    data: teams
  } = useOrgTeams(owner);
  const [selectedIndex, setSelectedIndex] = useState(0);
  let t1;
  if ($[0] !== owner || $[1] !== searchQuery || $[2] !== teams || $[3] !== users) {
    t1 = getMentionSuggestions(users, teams, owner, searchQuery);
    $[0] = owner;
    $[1] = searchQuery;
    $[2] = teams;
    $[3] = users;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const filteredSuggestions = t1;
  const effectiveSelectedIndex = selectedIndex < filteredSuggestions.length ? selectedIndex : 0;
  let t2;
  let t3;
  if ($[5] !== effectiveSelectedIndex || $[6] !== filteredSuggestions || $[7] !== onHighlight) {
    t2 = () => {
      if (onHighlight) {
        const highlightedSuggestion = filteredSuggestions[effectiveSelectedIndex];
        if (highlightedSuggestion) {
          onHighlight({
            id: highlightedSuggestion.handle,
            name: highlightedSuggestion.handle
          });
        } else {
          onHighlight(null);
        }
      }
    };
    t3 = [effectiveSelectedIndex, filteredSuggestions, onHighlight];
    $[5] = effectiveSelectedIndex;
    $[6] = filteredSuggestions;
    $[7] = onHighlight;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== onHighlight || $[11] !== onSelect) {
    t4 = suggestion => {
      onSelect({
        id: suggestion.handle,
        name: suggestion.handle
      });
      setSelectedIndex(0);
      onHighlight?.(null);
    };
    $[10] = onHighlight;
    $[11] = onSelect;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const onSelection = t4;
  let t5;
  let t6;
  if ($[13] !== effectiveSelectedIndex || $[14] !== filteredSuggestions || $[15] !== onSelection) {
    t5 = () => {
      const onKeyDown = e => {
        if (filteredSuggestions.length === 0) {
          return;
        }
        bb23: switch (e.key) {
          case "ArrowDown":
            {
              e.preventDefault();
              setSelectedIndex(prev_0 => {
                if (filteredSuggestions.length === 0) {
                  return 0;
                }
                const effectivePrev_0 = prev_0 < filteredSuggestions.length ? prev_0 : 0;
                return effectivePrev_0 < filteredSuggestions.length - 1 ? effectivePrev_0 + 1 : 0;
              });
              break bb23;
            }
          case "ArrowUp":
            {
              e.preventDefault();
              setSelectedIndex(prev => {
                if (filteredSuggestions.length === 0) {
                  return 0;
                }
                const effectivePrev = prev < filteredSuggestions.length ? prev : 0;
                return effectivePrev > 0 ? effectivePrev - 1 : filteredSuggestions.length - 1;
              });
              break bb23;
            }
          case "Enter":
          case "Tab":
            {
              e.preventDefault();
              e.stopPropagation();
              const selectedSuggestion = filteredSuggestions[effectiveSelectedIndex];
              if (selectedSuggestion) {
                onSelection(selectedSuggestion);
              }
            }
        }
      };
      document.addEventListener("keydown", onKeyDown, true);
      return () => {
        document.removeEventListener("keydown", onKeyDown, true);
      };
    };
    t6 = [filteredSuggestions, effectiveSelectedIndex, onSelection];
    $[13] = effectiveSelectedIndex;
    $[14] = filteredSuggestions;
    $[15] = onSelection;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useEffect(t5, t6);
  let t7;
  if ($[18] !== effectiveSelectedIndex || $[19] !== filteredSuggestions || $[20] !== onSelection) {
    t7 = <Command className="rounded-lg border shadow-md"><CommandList>{filteredSuggestions.length === 0 ? <CommandEmpty className="p-2 text-sm">No users or teams found.</CommandEmpty> : <CommandGroup>{filteredSuggestions.map((suggestion_0, index) => <MemoizedCommandItem key={suggestion_0.handle} suggestion={suggestion_0} isSelected={index === effectiveSelectedIndex} onSelect={() => onSelection(suggestion_0)} />)}</CommandGroup>}</CommandList></Command>;
    $[18] = effectiveSelectedIndex;
    $[19] = filteredSuggestions;
    $[20] = onSelection;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
});